'use client';
import React from 'react';
import { Zap, TrendingUp, Shield, Clock } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface Props {
  onGetSignal: () => void;
}

export default function SignalEmptyState({ onGetSignal }: Props) {
  const { t } = useLanguage();

  const features = [
    {
      id: 'feature-entry',
      icon: TrendingUp,
      label: t('featureEntryLabel'),
      desc: t('featureEntryDesc'),
      color: 'text-amber-400',
      bg: 'bg-amber-500/10',
    },
    {
      id: 'feature-tp',
      icon: Zap,
      label: t('featureTpLabel'),
      desc: t('featureTpDesc'),
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
    },
    {
      id: 'feature-sl',
      icon: Shield,
      label: t('featureSlLabel'),
      desc: t('featureSlDesc'),
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
    },
    {
      id: 'feature-timing',
      icon: Clock,
      label: t('featureTimingLabel'),
      desc: t('featureTimingDesc'),
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
    },
  ];

  return (
    <div className="rounded-2xl border border-white/5 bg-zinc-900/40 p-8 md:p-12">
      <div className="max-w-2xl mx-auto text-center space-y-6">
        {/* Icon */}
        <div className="flex justify-center">
          <div className="relative w-20 h-20 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0.05) 100%)', border: '1px solid rgba(212,175,55,0.2)' }}>
            <span className="text-3xl font-bold gold-text font-mono">Au</span>
            <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400/20 border border-amber-400/30 flex items-center justify-center">
              <Zap className="w-2.5 h-2.5 text-amber-400" />
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-2xl font-semibold text-zinc-100 mb-2">
            {t('noSignalYet')}
          </h2>
          <p className="text-sm text-zinc-500 leading-relaxed">
            {t('noSignalDesc')} <span className="text-amber-400 font-medium">{t('getSignal')}</span> {t('noSignalDescMid')}
          </p>
          <p className="text-xs text-amber-400/80 mt-2 font-medium">
            {t('noSignalRecommended')}
          </p>
        </div>

        {/* Feature grid */}
        <div className="grid grid-cols-2 gap-3 text-left">
          {features.map((f) => (
            <div
              key={f.id}
              className="flex items-start gap-3 p-3.5 rounded-xl bg-zinc-900/60 border border-white/5"
            >
              <div className={`w-8 h-8 rounded-lg ${f.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                <f.icon className={`w-4 h-4 ${f.color}`} />
              </div>
              <div>
                <p className={`text-xs font-semibold ${f.color}`}>{f.label}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}